import React from 'react';

import { FieldDisplayMode, IdeaFieldTypes } from 'common/constants/ideas';
import {
  type FieldFormState,
  type FieldSubmission,
  type FormulaFieldFormState,
  type ScoreFieldFormState,
  useIdeaFieldForm,
} from 'common/subdomain/admin/v2/hooks/useIdeaFieldForm';
import Button from 'common/uiV3/Button';
import Dialog from 'common/uiV3/Dialog';

import BaseFieldInputs from './form/BaseFieldInputs';
import DisplayModeInputs from './form/DisplayModeInputs';
import FormulaFieldInputs from './form/FormulaFieldInputs';
import RangeFieldInputs from './form/RangeFieldInputs';
import ScoreFieldInputs from './form/ScoreFieldInputs';

import type { CustomIdeaField, FormulaIdeaField, ScoreIdeaField } from 'common/api/endpoints/idea';

type Props = {
  isOpen: boolean;
  loading: boolean;
  /**
   * Called as soon as the modal is closing.
   */
  onClose: () => void;
  /**
   * Called when the modal close animation is complete.
   */
  onClosed?: () => void;
  onSubmit: (field: FieldSubmission) => void;
  types?: IdeaFieldTypes[];
  field?: CustomIdeaField | FormulaIdeaField | ScoreIdeaField;
  onDelete: () => void;
};

const isFormulaField = (formState: FieldFormState): formState is FormulaFieldFormState =>
  formState.type === IdeaFieldTypes.formula;

const isScoreField = (formState: FieldFormState): formState is ScoreFieldFormState =>
  formState.type === IdeaFieldTypes.score;

export default function AdminV2CustomIdeaFieldModal(props: Props) {
  const { onClose, loading, onSubmit, field, types, isOpen, onClosed } = props;
  const isEditing = !!field;

  const { formState, isValid, handleSubmit, setValue, resetForm } = useIdeaFieldForm(field);

  const handleClosed = () => {
    if (onClosed) {
      onClosed();
    }
    resetForm();
  };

  const handleDelete = () => {
    if (loading || !isEditing) {
      return;
    }

    props.onDelete();
  };

  const headerText = isEditing ? 'Edit field' : 'Create new field';
  const FieldsWithDisplayModes = [
    IdeaFieldTypes.number,
    IdeaFieldTypes.formula,
    IdeaFieldTypes.score,
  ];

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Content
        className="sm:max-w-[480px]"
        onCloseAutoFocus={handleClosed}
        // This is needed because for formula fields,
        // we want to prevent the modal from closing when the segment creation modal is closing
        onInteractOutside={(e) => e.preventDefault()}>
        <Dialog.Header>
          <Dialog.Title>{headerText}</Dialog.Title>
          <Dialog.Description className="sr-only">
            {isEditing ? 'Edit field details' : 'Create a new field.'}
          </Dialog.Description>
        </Dialog.Header>
        <Dialog.Body>
          <BaseFieldInputs
            types={types}
            loading={loading}
            formState={formState}
            readOnly={isEditing}
            setValue={setValue}
          />
          {FieldsWithDisplayModes.includes(formState.type!) && (
            <DisplayModeInputs
              defaultMode={
                `displayMode` in formState ? formState.displayMode : FieldDisplayMode.standard
              }
              setValue={setValue}
            />
          )}
          {formState.type === IdeaFieldTypes.range && (
            <RangeFieldInputs
              formState={formState}
              setValue={setValue}
              readOnly={isEditing}
              loading={loading}
            />
          )}
          {isFormulaField(formState) && (
            <FormulaFieldInputs
              formulaFieldState={formState}
              readOnly={isEditing}
              setValue={setValue}
            />
          )}
          {isScoreField(formState) && (
            <ScoreFieldInputs scoreFieldState={formState} setValue={setValue} />
          )}
        </Dialog.Body>
        <Dialog.Footer className="justify-between">
          {isEditing ? (
            <Button color="error" disabled={loading} variant="outlined" onClick={handleDelete}>
              Delete
            </Button>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <Dialog.Close asChild>
              <Button disabled={loading} variant="outlined">
                Cancel
              </Button>
            </Dialog.Close>
            <Button disabled={!isValid} onClick={() => handleSubmit(onSubmit)} isLoading={loading}>
              {isEditing ? 'Save' : 'Create'}
            </Button>
          </div>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
}
